import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuX } from 'react-icons/lu';
import { computePlacement } from './position';
import './tour.css';

const CARD_WIDTH = 360;
const MOBILE_QUERY = '(max-width: 767px)';

export const isMobileViewport = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.(MOBILE_QUERY).matches;

const viewportSize = () => ({ width: window.innerWidth, height: window.innerHeight });

const toRect = (el) => {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

/** Follows the target through scrolling, resizing and layout shifts. */
function useTrackedRect(target, onLost) {
  const [rect, setRect] = useState(() => (target ? toRect(target) : null));
  const onLostRef = useRef(onLost);
  onLostRef.current = onLost;

  useLayoutEffect(() => {
    if (!target) {
      setRect(null);
      return undefined;
    }
    setRect(toRect(target));
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!target.isConnected) {
          onLostRef.current?.();
          return;
        }
        const next = toRect(target);
        setRect((prev) => (prev && prev.top === next.top && prev.left === next.left
          && prev.width === next.width && prev.height === next.height ? prev : next));
      });
    };
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    resizeObserver?.observe(target);
    // A parent re-render can swap the node out without any resize or scroll.
    const mutationObserver = typeof MutationObserver !== 'undefined' ? new MutationObserver(update) : null;
    mutationObserver?.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [target]);

  return rect;
}

function useViewport() {
  const [state, setState] = useState(() => ({ ...viewportSize(), isMobile: isMobileViewport() }));
  useEffect(() => {
    const onResize = () => setState({ ...viewportSize(), isMobile: isMobileViewport() });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return state;
}

const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/**
 * Spotlight + card for one step. Purely presentational: the provider decides
 * which step is showing and what the buttons do.
 */
export default function TourOverlay({
  step, target, pending = false, progress, isFirst, isLast, showMute, t, dir, vars,
  onNext, onBack, onSkip, onMute, onTargetLost,
}) {
  const cardRef = useRef(null);
  const primaryRef = useRef(null);
  const viewport = useViewport();
  const rect = useTrackedRect(target, onTargetLost);
  const [cardSize, setCardSize] = useState({ width: CARD_WIDTH, height: 220 });

  const isIntro = step.variant === 'intro';
  const padding = step.padding ?? 6;
  const title = t(step.title, undefined, vars);
  const body = t(step.body, undefined, vars);

  // Measure the rendered card so placement uses its real height.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const next = { width: el.offsetWidth, height: el.offsetHeight };
    setCardSize((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
  }, [step, title, body, viewport.width, viewport.isMobile]);

  // Move focus into the card on every step so keyboard users follow along.
  useEffect(() => {
    if (!pending) primaryRef.current?.focus({ preventScroll: true });
  }, [step, pending]);

  // Keyboard: Esc skips, arrows move (mirrored in RTL), Tab stays in the card.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onSkip();
        return;
      }
      const inField = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName || '');
      if (!pending && !inField && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        const forward = (e.key === 'ArrowRight') !== (dir === 'rtl');
        e.preventDefault();
        if (forward) onNext();
        else if (!isFirst) onBack();
        return;
      }
      if (e.key === 'Tab' && cardRef.current) {
        const items = [...cardRef.current.querySelectorAll(FOCUSABLE)];
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (!cardRef.current.contains(active)) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    // Capture phase so page-level Esc handlers (drawers, dropdowns) don't also fire.
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [dir, isFirst, pending, onBack, onNext, onSkip]);

  const spotRect = rect && {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };

  const sheet = viewport.isMobile;
  const placement = sheet ? null : computePlacement({
    target: spotRect,
    card: cardSize,
    viewport,
    placement: step.placement || 'bottom',
    dir,
  });

  const cardStyle = sheet
    ? { left: 16, right: 16, bottom: 16 }
    : { top: placement.top, left: placement.left, width: Math.min(CARD_WIDTH, viewport.width - 24) };

  const arrowStyle = !sheet && placement.side && {
    bottom: { top: -6, left: placement.arrowOffset - 7 },
    top: { bottom: -6, left: placement.arrowOffset - 7 },
    right: { left: -6, top: placement.arrowOffset - 7 },
    left: { right: -6, top: placement.arrowOffset - 7 },
  }[placement.side];

  const spotlightStyle = spotRect || {
    top: viewport.height / 2,
    left: viewport.width / 2,
    width: 0,
    height: 0,
  };

  const Icon = step.icon;
  const titleId = 'xb-tour-title';
  const bodyId = 'xb-tour-body';

  return createPortal(
    <div className="xb-tour-layer" dir={dir}>
      <div className="xb-tour-blocker" aria-hidden="true" />
      <div className="xb-tour-spotlight" data-empty={!spotRect} style={spotlightStyle} aria-hidden="true" />

      {!pending && (
      <div
        ref={cardRef}
        key={step.id}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        data-tour-card=""
        data-sheet={sheet}
        tabIndex={-1}
        className={`xb-tour-card ${isIntro ? 'p-7' : 'p-6'}`}
        style={cardStyle}
      >
        {arrowStyle && <span className="xb-tour-arrow" style={arrowStyle} aria-hidden="true" />}

        <button
          type="button"
          onClick={onSkip}
          aria-label={t('tour.close')}
          className="xb-tour-close absolute top-4 end-4 w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
        >
          <LuX className="w-5 h-5" aria-hidden="true" />
        </button>

        {Icon && <Icon className={`${isIntro ? 'w-10 h-10' : 'w-8 h-8'} mb-4 text-white`} strokeWidth={1.75} aria-hidden="true" />}
        <h2 id={titleId} className={`m-0 pe-8 font-bold tracking-tight text-white ${isIntro ? 'text-[22px]' : 'text-lg'}`}>
          {title}
        </h2>
        <p id={bodyId} className="mt-2 mb-0 text-[15px] leading-relaxed text-white/80">
          {body}
        </p>

        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 text-sm text-white/60">
            {progress && <span className="whitespace-nowrap tabular-nums">{t('tour.progress', undefined, progress)}</span>}
            {showMute && (
              <button type="button" onClick={onMute} className="xb-tour-ghost truncate text-white/60 hover:text-white underline-offset-4 hover:underline transition-colors">
                {t('tour.turn_off_tips')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {isIntro ? (
              <button type="button" onClick={onSkip} className="xb-tour-ghost h-10 px-4 rounded-full text-sm font-semibold text-white/80 hover:text-white hover:bg-white/10 transition-colors">
                {t('tour.maybe_later')}
              </button>
            ) : !isFirst && (
              <button type="button" onClick={onBack} className="xb-tour-ghost h-10 px-4 rounded-full text-sm font-semibold text-white/80 hover:text-white hover:bg-white/10 transition-colors">
                {t('tour.back')}
              </button>
            )}
            <button
              ref={primaryRef}
              type="button"
              onClick={onNext}
              className="xb-tour-primary h-10 px-5 rounded-full bg-white text-[#1f1f1f] text-sm font-semibold hover:bg-white/90 active:scale-[0.98] transition"
            >
              {isIntro ? t('tour.start') : isLast ? t('tour.finish') : t('tour.next')}
            </button>
          </div>
        </div>

        {progress && (
          <span className="sr-only" aria-live="polite">
            {`${t('tour.progress', undefined, progress)}: ${title}`}
          </span>
        )}
      </div>
      )}
    </div>,
    document.body,
  );
}
